import {
  FieldRequirement, FieldRequirementEnvironment, FieldRequirementResolver, FieldRequirementResolverMap,
} from '../../../models/fieldRules'

const getParentPath = (path: string) => {
  const parts = path.split('.')
  if (parts.length <= 1) return null
  parts.pop()
  return parts.join('.')
}

function createGetEffectiveServerRequirement<TContext>(
  getServerRequirement?: (path: string, ctx: TContext) => FieldRequirement | null,
) {
  return (path: string, ctx: TContext): FieldRequirement | null => {
    if (!getServerRequirement) return null

    const requirement = getServerRequirement(path, ctx)
    if (requirement) return requirement

    let parent = getParentPath(path)
    while (parent) {
      const parentReq = getServerRequirement(parent, ctx)
      if (parentReq === 'forbidden') {
        return 'forbidden'
      }
      parent = getParentPath(parent)
    }

    return null
  }
}

export function matchesPattern(pattern: string, path: string): boolean {
  if (pattern === path) return true

  const patternParts = pattern.split('.')
  const pathParts = path.split('.')

  if (patternParts.length !== pathParts.length) return false

  for (let i = 0; i < patternParts.length; i++) {
    const patternPart = patternParts[i]
    const pathPart = pathParts[i]
    if (patternPart === '*') continue
    if (patternPart !== pathPart) return false
  }

  return true
}

/**
 * Looks up a server-provided field requirement for a concrete path.
 *
 * The server keys requirements with `*` wildcards for array elements
 * (e.g. `previousDocument.*.documentType`), while `path` is a concrete
 * indexed path (e.g. `previousDocument.0.documentType`). An exact-key
 * lookup is tried first, then the wildcard patterns are matched, so that
 * per-field server requirements are honored for every array index instead
 * of silently falling through to the parent/default.
 */
export function matchServerRequirement(
  fieldRequirements: Record<string, FieldRequirement> | null | undefined,
  path: string,
): FieldRequirement | null {
  if (!fieldRequirements) return null

  const exact = fieldRequirements[path]
  if (exact) return exact

  for (const [pattern, requirement] of Object.entries(fieldRequirements)) {
    if (pattern.includes('*') && matchesPattern(pattern, path)) {
      return requirement
    }
  }

  return null
}

export function createFieldRequirementGetter<TContext>(
  resolvers: FieldRequirementResolverMap<TContext>,
  getServerRequirement?: (path: string, ctx: TContext) => FieldRequirement | null,
): (path: string, ctx: TContext) => FieldRequirement | null {
  function getFieldRequirementResolverForPattern(p: string) {
    for (const [pattern, resolver] of Object.entries(resolvers)) {
      if (!pattern.includes('*')) continue
      if (matchesPattern(pattern, p)) {
        return resolver
      }
    }

    return null
  }

  const getEffectiveServerRequirement = createGetEffectiveServerRequirement(getServerRequirement)

  return (path: string, ctx: TContext): FieldRequirement | null => {
    const cache = new Map<string, FieldRequirement | null>()

    const skipServerRequirementPaths = new Set<string>()
    function isSkipServerRequirementForPath(p: string) {
      let current: string | null = p
      while (current) {
        if (skipServerRequirementPaths.has(current)) return true
        current = getParentPath(current)
      }
      return false
    }

    function innerGetFieldRequirement(p: string): FieldRequirement | null {
      const cachedRequirement = cache.get(p)
      if (cachedRequirement) {
        return cachedRequirement
      }

      let resolverForPath: FieldRequirementResolver<TContext> | null = resolvers[p]

      if (!resolverForPath) {
        resolverForPath = getFieldRequirementResolverForPattern(p)
      }

      let skipServerRequirement = false

      let result: FieldRequirement | null

      if (!resolverForPath) {
        const parent = getParentPath(p)
        if (parent) {
          result = innerGetFieldRequirement(parent)
        } else {
          result = null
        }
      } else {
        const env: FieldRequirementEnvironment<TContext> = {
          ctx,
          getFieldRequirement: innerGetFieldRequirement,
          ignoreServerRequirement: () => {
            skipServerRequirement = true
            skipServerRequirementPaths.add(p)
          },
        }

        result = resolverForPath(env)
      }

      let parent = getParentPath(p)
      while (parent) {
        const parentReq = innerGetFieldRequirement(parent)
        if (parentReq === 'forbidden') {
          result = 'forbidden'
          break
        }
        parent = getParentPath(parent)
      }

      const shouldSkipServerRequirement = skipServerRequirement || isSkipServerRequirementForPath(p)
      if (!shouldSkipServerRequirement) {
        const serverRequirement = getEffectiveServerRequirement(p, ctx)
        if (serverRequirement) {
          result = serverRequirement
        }
      }

      cache.set(p, result)
      return result
    }

    return innerGetFieldRequirement(path)
  }
}

export function createGetFieldRequirementWithParentLookup<TContext>(
  baseFn: (path: string, ctx: TContext) => FieldRequirement | null,
): (path: string, ctx: TContext) => FieldRequirement {
  return (path, ctx): FieldRequirement => {
    const requirement = baseFn(path, ctx)
    if (requirement === 'forbidden') return 'forbidden'

    let parentPath = getParentPath(path)
    while (parentPath) {
      const parentRequirement = baseFn(parentPath, ctx) ?? 'optional'
      if (parentRequirement === 'forbidden') return 'forbidden'
      parentPath = getParentPath(parentPath)
    }

    return requirement ?? 'optional'
  }
}
